import React, { useMemo, useState } from "react";
import { Search, X, CheckCircle2 } from "lucide-react";

/* Reusable checkbox card (TrainerRegister bhi isi ko import karega) */
export const CheckOption = ({ label, selected, onToggle }) => (
    <label className={`trainer-tech-option ${selected ? "active" : ""}`}>
        <input type="checkbox" checked={selected} onChange={onToggle} />
        <span className="trainer-tech-check">
            {selected && <CheckCircle2 size={16} />}
        </span>
        <span className="trainer-tech-name">{label}</span>
    </label>
);

const norm = (value) => String(value).toLowerCase().trim();

/*
  sections = [
    {
      id: "Civil Engineering",
      title: "Civil Engineering",
      blocks: [
        { heading: "Structural Engineering" | null, items: ["RCC Design", ...] }
      ]
    }
  ]
  - 1 section  -> sirf search bar
  - 2+ sections -> search bar + branch tabs
*/
function SkillPicker({ sections, selected, onToggle, onClearAll }) {
    const [query, setQuery] = useState("");
    const [activeSection, setActiveSection] = useState("all");

    const q = norm(query);
    const hasTabs = sections.length > 1;

    const tabs = useMemo(
        () =>
            sections.map((section) => {
                const unique = new Set(section.blocks.flatMap((b) => b.items));
                return {
                    id: section.id,
                    title: section.title,
                    selectedCount: [...unique].filter((item) =>
                        selected.includes(item)
                    ).length,
                };
            }),
        [sections, selected]
    );

    const visibleSections = useMemo(
        () =>
            sections
                .filter(
                    (section) =>
                        activeSection === "all" || section.id === activeSection
                )
                .map((section) => {
                    const blocks = section.blocks
                        .map((block) => {
                            const headingMatch =
                                block.heading && norm(block.heading).includes(q);

                            const items =
                                !q || headingMatch
                                    ? block.items
                                    : block.items.filter((item) =>
                                          norm(item).includes(q)
                                      );

                            return { ...block, items };
                        })
                        .filter((block) => block.items.length > 0);

                    return { ...section, blocks };
                })
                .filter((section) => section.blocks.length > 0),
        [sections, activeSection, q]
    );

    const totalVisible = visibleSections.reduce(
        (sum, section) =>
            sum + section.blocks.reduce((s, b) => s + b.items.length, 0),
        0
    );

    return (
        <div className="trainer-skill-picker">
            {/* ---------- SEARCH ---------- */}
            <div className="trainer-search-box">
                <Search size={18} />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search subject, technology or course..."
                />
                {query && (
                    <button
                        type="button"
                        className="trainer-search-clear"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                    >
                        <X size={15} />
                    </button>
                )}
            </div>

            {/* ---------- BRANCH TABS ---------- */}
            {hasTabs && (
                <div className="trainer-branch-tabs">
                    <button
                        type="button"
                        className={`trainer-branch-tab ${
                            activeSection === "all" ? "active" : ""
                        }`}
                        onClick={() => setActiveSection("all")}
                    >
                        All Branches
                    </button>

                    {tabs.map((tab) => (
                        <button
                            type="button"
                            key={tab.id}
                            className={`trainer-branch-tab ${
                                activeSection === tab.id ? "active" : ""
                            }`}
                            onClick={() => setActiveSection(tab.id)}
                        >
                            {tab.title}
                            {tab.selectedCount > 0 && <b>{tab.selectedCount}</b>}
                        </button>
                    ))}
                </div>
            )}

            {/* ---------- META ---------- */}
            <div className="trainer-skill-meta">
                <span>
                    {totalVisible} option{totalVisible === 1 ? "" : "s"}
                    {q ? ` found for "${query.trim()}"` : ""}
                </span>

                {selected.length > 0 && (
                    <button
                        type="button"
                        className="trainer-clear-all"
                        onClick={onClearAll}
                    >
                        Clear all ({selected.length})
                    </button>
                )}
            </div>

            {/* ---------- LIST ---------- */}
            <div className="trainer-skill-scroll">
                {visibleSections.length === 0 ? (
                    <div className="trainer-skill-empty">
                        No match found. Try a different keyword.
                    </div>
                ) : (
                    visibleSections.map((section) => (
                        <div key={section.id} className="trainer-skill-group">
                            {hasTabs && (
                                <h4 className="trainer-skill-group-title">
                                    {section.title}
                                </h4>
                            )}

                            {section.blocks.map((block) => (
                                <div
                                    key={`${section.id}-${block.heading || "list"}`}
                                    className="trainer-skill-block"
                                >
                                    {block.heading && (
                                        <div className="trainer-skill-subject">
                                            {block.heading}
                                        </div>
                                    )}

                                    <div className="trainer-technology-grid">
                                        {block.items.map((item) => (
                                            <CheckOption
                                                key={`${section.id}-${block.heading}-${item}`}
                                                label={item}
                                                selected={selected.includes(item)}
                                                onToggle={() => onToggle(item)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default SkillPicker;
